import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { Spinner } from 'flowbite-react';
import * as wasm from 'sched';
import { DependencyNodeId } from 'sched';
import { WorkspaceContext } from '../../context/WorkspaceContext';

// Props for the component
interface DependencyNodeSearchInputProps {
  onSelect: (nodeId: DependencyNodeId, title: string) => void;
  placeholder?: string;
  clearOnChange?: any;
  className?: string;
  initialValue?: string;
}

const DependencyNodeSearchInput: React.FC<DependencyNodeSearchInputProps> = ({
  onSelect,
  placeholder = "Search for a task or outcome...",
  clearOnChange = {},
  className = "",
  initialValue = ""
}) => {
  const { safeData } = React.useContext(WorkspaceContext);
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle clicking outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset search when clearOnChange changes
  useEffect(() => {
    setSearchTerm('');
    setShowDropdown(false);
  }, [clearOnChange]);

  // Set up popper for positioning
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const { styles, attributes, update } = usePopper(inputRef.current, popperElement, {
    placement: 'bottom-start',
    modifiers: [
      { name: 'offset', options: { offset: [0, 5] } },
      { name: 'preventOverflow', options: { padding: 8 } }
    ],
  });
  
  // Update popper position when dropdown visibility changes
  useEffect(() => {
    if (showDropdown && update) {
      update();
    }
  }, [showDropdown, update]);
  
  // Force update on scroll and resize
  useEffect(() => {
    if (!showDropdown || !update) return;
    
    const handleScroll = () => update();
    const handleResize = () => update();
    
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [showDropdown, update]);

  // Fetch search results when search term changes
  useEffect(() => {
    const fetchSearchResults = async () => {
      if (!searchTerm || !safeData) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      
      // Set searching state before any async operations
      setIsSearching(true);
      try {
        // Use the WASM search_dependencies function
        const results = await wasm.search_dependencies(safeData, searchTerm.toLowerCase(), 10);
        
        // Transform results to a more convenient format
        const formattedResults = results.map(result => {
          try {
            // Access the dependency and project using the WASM bindings
            const dependency = result.dependency();
            const project = result.project();
            
            // Check if it's a task, outcome, decision, or workflow instance
            const task = dependency.as_task();
            const outcome = dependency.as_outcome();
            const decision = dependency.as_decision();
            const workflowInstance = dependency.as_workflow_instance();

            if (task) {
              return {
                nodeId: { Task: task.id() },
                title: task.title(),
                description: task.description(),
                project_name: project ? project.name() : null,
                type: 'task' as const
              };
            } else if (outcome) {
              return {
                nodeId: { Outcome: outcome.id() },
                title: outcome.title(),
                description: outcome.description(),
                project_name: project ? project.name() : null,
                type: 'outcome' as const
              };
            } else if (decision) {
              return {
                nodeId: { Decision: decision.id() },
                title: decision.title(),
                description: null,
                project_name: null,
                type: 'decision' as const
              };
            } else if (workflowInstance) {
              return {
                nodeId: { WorkflowInstance: workflowInstance.id() },
                title: `Workflow: ${workflowInstance.template_id()}`,
                description: null,
                project_name: null,
                type: 'workflow_instance' as const
              };
            } else {
              throw new Error('Result is neither a task, outcome, decision, nor workflow instance');
            }
          } catch (error) {
            console.error('Error processing search result:', error);
            return {
              nodeId: null,
              title: 'Error loading item',
              description: null,
              project_name: null,
              type: 'error'
            };
          }
        });
        
        // Filter out any results with null nodeId
        setSearchResults(formattedResults.filter(result => result.nodeId !== null));
      } catch (error) {
        console.error('Error searching dependencies:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
        setHasSearched(true);
      }
    };
    
    // Set searching state immediately when search term changes
    if (searchTerm && safeData) {
      setIsSearching(true);
    }
    
    // Debounce the search to avoid too many requests
    const debounceTimeout = setTimeout(fetchSearchResults, 300);
    
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, safeData]);

  return (
    <div className={`relative w-full ${className}`} style={{ overflow: 'visible' }}>
      <input
        ref={inputRef}
        type="text"
        className="bg-cm-bg-elevated border border-cm-border-subtle text-cm-text-primary text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:placeholder-gray-400 dark:focus:ring-blue-500 dark:focus:border-blue-500"
        placeholder={placeholder}
        value={searchTerm}
        onChange={(e) => {
          const newValue = e.target.value;
          setSearchTerm(newValue);
          setShowDropdown(true);
          
          // Reset search state when input changes
          if (newValue) {
            setIsSearching(true);
            setHasSearched(false);
          }
        }}
        onFocus={() => setShowDropdown(true)}
      />
      
      {/* Dropdown results rendered with Portal and Popper */}
      {showDropdown && searchTerm && createPortal(
        <div 
          ref={(el) => {
            dropdownRef.current = el;
            setPopperElement(el);
          }}
          className="z-50 bg-cm-bg-surface rounded-lg border border-cm-border-subtle max-h-60 overflow-auto shadow-lg"
          style={{
            ...styles.popper,
            width: inputRef.current?.offsetWidth,
          }}
          {...attributes.popper}
        >
          {isSearching ? (
            <div className="px-4 py-2 text-cm-text-muted flex items-center justify-center gap-2">
              <Spinner size="sm" />
              <span>Searching...</span>
            </div>
          ) : searchResults.length > 0 ? (
            searchResults.map((result, index) => (
              <DependencyNodeDropdownItem 
                key={`${result.type}-${index}`}
                result={result}
                onClick={() => {
                  onSelect(result.nodeId, result.title);
                  setSearchTerm(result.title);
                  setShowDropdown(false);
                }}
              />
            ))
          ) : (
            <div className="px-4 py-2 text-cm-text-muted">
              {searchTerm && hasSearched ? 'No matching items found' : 'Type to search...'}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

// Props for the dropdown item component
interface DependencyNodeDropdownItemProps {
  result: {
    nodeId: DependencyNodeId;
    title: string;
    description?: string | null;
    project_name?: string | null;
    type: 'task' | 'outcome' | 'decision' | 'workflow_instance' | 'error';
  };
  onClick: () => void;
}

const DependencyNodeDropdownItem: React.FC<DependencyNodeDropdownItemProps> = ({ result, onClick }) => {
  // Truncate description to a single line
  const truncateDescription = (text: string | null, maxLength = 60) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Get an icon or indicator for the type
  const getTypeIndicator = () => {
    if (result.type === 'task') {
      return <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded dark:bg-blue-900">Task</span>;
    } else if (result.type === 'outcome') {
      return <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-800 rounded dark:bg-green-900">Outcome</span>;
    } else if (result.type === 'decision') {
      return <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-800 rounded dark:bg-purple-900 dark:text-purple-300">Decision</span>;
    } else if (result.type === 'workflow_instance') {
      return <span className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-800 rounded dark:bg-orange-900 dark:text-orange-300">Workflow</span>;
    }
    return null;
  };

  return (
    <div 
      className="px-4 py-2 cursor-pointer hover:bg-cm-bg-hover"
      onClick={onClick}
    >
      <div className="flex justify-between items-start">
        <div className="flex-grow">
          <div className="font-medium flex items-center gap-2">
            {result.title}
            {getTypeIndicator()}
          </div>
          {result.description && (
            <div className="text-xs text-cm-text-muted mt-1">
              {truncateDescription(result.description)}
            </div>
          )}
        </div>
        <div className="ml-2 text-xs text-cm-text-muted min-w-[80px] text-right">
          {result.project_name || 'No project'}
        </div>
      </div>
    </div>
  );
};

export default DependencyNodeSearchInput;
